import type { StyleSpecification } from 'maplibre-gl';
import { defaultProvider, providerById, type BasemapProvider } from './providers';

/**
 * Basemap and terrain endpoints come from the environment, so the imagery
 * provider is a deployment decision rather than something baked into the code.
 * Check the licence terms of whatever you point these at.
 */
export const basemapConfig = {
  satelliteUrl: import.meta.env.VITE_BASEMAP_SATELLITE_URL as string | undefined,
  satelliteAttribution:
    (import.meta.env.VITE_BASEMAP_SATELLITE_ATTRIBUTION as string | undefined) ?? '',
  labelsUrl: import.meta.env.VITE_BASEMAP_LABELS_URL as string | undefined,
  demUrl: import.meta.env.VITE_TERRAIN_DEM_URL as string | undefined,
  demEncoding: (import.meta.env.VITE_TERRAIN_DEM_ENCODING as 'terrarium' | 'mapbox' | undefined) ??
    'terrarium',
};

export const BASEMAP_SOURCE = 'basemap';
export const LABELS_SOURCE = 'basemap-labels';
export const DEM_SOURCE = 'terrain-dem';
export const FIELD_SOURCE = 'field';
export const INDEX_SOURCE = 'index-raster';

export const LAYER = {
  basemap: 'basemap-raster',
  labels: 'basemap-labels-raster',
  index: 'index-raster-layer',
  fieldFill: 'field-fill',
  fieldOutline: 'field-outline',
  fieldOutlineGlow: 'field-outline-glow',
  hillshade: 'terrain-hillshade',
} as const;

/** Surface colour shown before any basemap tile has painted. */
const BACKGROUND = '#0d1117';

/**
 * Builds the style for one provider.
 *
 * The provider registry decides the tile template, its axis order, its size
 * and its attribution. An explicit VITE_BASEMAP_SATELLITE_URL still wins, so a
 * deployment can point at its own tile service without touching code.
 */
export function buildStyle(providerId?: string | null): StyleSpecification {
  const sources: StyleSpecification['sources'] = {};
  const layers: StyleSpecification['layers'] = [
    { id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } },
  ];

  const provider: BasemapProvider | null = providerById(providerId) ?? defaultProvider();

  // An explicitly configured URL overrides the registry entirely.
  const url = basemapConfig.satelliteUrl ?? provider?.url;
  const tileSize = basemapConfig.satelliteUrl ? 256 : (provider?.tileSize ?? 256);
  const maxzoom = basemapConfig.satelliteUrl ? 19 : (provider?.maxzoom ?? 19);
  const attribution = basemapConfig.satelliteUrl
    ? basemapConfig.satelliteAttribution
    : (provider?.attribution ?? '');

  if (url) {
    sources[BASEMAP_SOURCE] = {
      type: 'raster',
      tiles: [url],
      tileSize,
      maxzoom,
      attribution,
    };
    layers.push({ id: LAYER.basemap, type: 'raster', source: BASEMAP_SOURCE });
  }

  if (basemapConfig.labelsUrl) {
    sources[LABELS_SOURCE] = {
      type: 'raster',
      tiles: [basemapConfig.labelsUrl],
      tileSize: 256,
      maxzoom: 19,
    };
    layers.push({ id: LAYER.labels, type: 'raster', source: LABELS_SOURCE });
  }

  return {
    version: 8,
    sources,
    layers,
  } as StyleSpecification;
}

export const basemapConfigured = (): boolean =>
  Boolean(basemapConfig.satelliteUrl) || defaultProvider() !== null;
export const terrainConfigured = (): boolean => Boolean(basemapConfig.demUrl);
